import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { IInputStep, IInputStepApplication } from 'interfaces/IInputStep';
import {
  IInputTask,
  IInputTaskGeneric,
  InputTaskType,
} from 'interfaces/IInputTask';
import { resolveAreaHa } from 'pages/FarmsPage/Workspace/Insights/insightUtils';

// Other costs (OP-2399): a cost that isn't an application, such as insurance
// or cartage. Each is a generic task with a date, an area (the plan's to
// start with, editable) and Operation inputs, but no timing or application
// details. Its costs are Operation inputs, so they show under the Operation
// filter and count as Operation spend everywhere. The user enters a total or
// $/ha; it is stored as $/ha at a rate of 1 over the task's area, so the
// total follows the area if it changes.

export type OtherCostEntryMode = 'total' | 'perHa';

export const isOtherCostTask = (task: IInputTask): task is IInputTaskGeneric =>
  task.taskType === InputTaskType.Generic;

/** Folds a catalogue rate into the cost, so the step reads as $/ha at rate 1. */
export const toOtherCostStep = (
  step: IInputStepApplication
): IInputStepApplication => ({
  ...step,
  rate: 1,
  // No unit: the rate is only how the cost is held, not a "$/ha" amount.
  appUnit: null,
  cost: step.cost === null ? null : (step.rate ?? 1) * step.cost,
});

/** The area a cost is worked over, in hectares: the task's, else the plan's. */
export const getOtherCostAreaHa = (
  step: IInputStep,
  task: IInputTask,
  input: IInput,
  gUnits: GUnit[]
): number => resolveAreaHa(step, task, input, gUnits);

/** The amount shown in the chosen mode, or null when no cost is entered. */
export const getOtherCostAmount = (
  costPerHa: number | null,
  mode: OtherCostEntryMode,
  areaHa: number
): number | null => {
  if (costPerHa === null) return null;
  return mode === 'total' ? costPerHa * areaHa : costPerHa;
};

/** The $/ha to store for an amount entered in the chosen mode. */
export const toOtherCostPerHa = (
  amount: number | null,
  mode: OtherCostEntryMode,
  areaHa: number
): number | null => {
  if (amount === null) return null;
  if (mode === 'perHa') return amount;
  return areaHa > 0 ? amount / areaHa : null;
};
